import {
  ROLE_NUMBER_ADMINISTRATOR, ROLE_NUMBER_CI, ROLE_NUMBER_CI_MANAGER, ROLE_NUMBER_CLINICAL, ROLE_NUMBER_CLINICAL_CI,
  ROLE_NUMBER_CLINICAL_MANAGER, ROLE_NUMBER_GLOBAL_ADMIN, ROLE_NUMBER_MDS_NURSE, ROLE_NUMBER_MDS_REGIONAL,
  ROLE_NUMBER_MINIMAL_CI, ROLE_NUMBER_SOCIAL_WORKER, ROLE_NUMBER_TRIPLE_CHECK,
} from 'list-types-public';
import { KPI_ROOT, NAV_ROUTES, TITLES } from './routes';
import { NavCtx, RolePreset } from './types';

const T = TITLES;

// No Profile: /client is the legacy underwriting shell this role has no business in.
const tripleCheck: string[] = [T.tripleCheckReport, T.logOut];

const minimalCi: string[] = [T.createReferral, T.referralInbox, T.users, T.profile, T.logOut];

// Discharge Plan only, plus the board it is a view over. The dischargePlanEnabled filter below
// still applies: a region that loses the feature leaves this role the bed board alone.
const socialWorker: string[] = [T.liveBedBoard, T.dischargePlan, T.profile, T.logOut];

// MDS roles: no referral intake, Discharge Plan or admin. Nurse drops Census Trends.
const mdsRegional: string[] = [
  T.managerDashboard, T.liveBedBoard, T.censusTrends, T.liveCensus, T.hospitalTracker, T.returningReferrals,
  T.adtReport, T.tripleCheckReport, T.averageLosReport, T.referralConversions, T.referralsSummary,
  T.assessmentReport, T.payerChangeReport, T.complaiReports, T.mdsReports, T.complaiDashboard, T.mdsDashboard,
  T.eventReports, T.profile, T.logOut,
];

const mdsNurse: string[] = mdsRegional.filter((t) => t !== T.censusTrends);


// Discharge Plan rides with Live Bed Board everywhere the board is offered: it
// is a view over the board's own per-patient plans, not a separate product.
const admin: string[] = [
  T.createReferral, T.referralInbox, T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.highRiskPatients, T.liveCensus,
  T.censusTrends, T.hospitalTracker, T.returningReferrals,
  T.adtReport, T.kpis, T.tripleCheckReport, T.averageLosReport, T.referralConversions, T.referralsSummary,
  T.referralResponseReport, T.declineInsights,
  T.assessmentReport, T.payerChangeReport, T.complaiReports, T.mdsReports, T.complaiDashboard, T.mdsDashboard, T.eventReports,
  T.secondApproval, T.users, T.profile, T.admin, T.logOut,
];

const report: string[] = [
  T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.liveCensus, T.censusTrends, T.hospitalTracker,
  T.returningReferrals, T.adtReport, T.kpis, T.tripleCheckReport, T.averageLosReport, T.referralConversions,
  T.referralsSummary, T.referralResponseReport, T.declineInsights, T.assessmentReport, T.payerChangeReport,
  T.complaiReports, T.mdsReports, T.complaiDashboard, T.mdsDashboard, T.eventReports, T.profile, T.logOut,
];

const clinical: string[] = [
  T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.liveCensus, T.hospitalTracker, T.adtReport, T.kpis,
  T.assessmentReport, T.tripleCheckReport, T.averageLosReport, T.complaiReports, T.mdsReports, T.complaiDashboard, T.mdsDashboard, T.eventReports,
  T.profile, T.logOut,
];

const clinicalManager: string[] = [
  T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.liveCensus, T.hospitalTracker, T.adtReport, T.kpis,
  T.assessmentReport, T.tripleCheckReport, T.averageLosReport, T.complaiReports, T.mdsReports, T.complaiDashboard, T.mdsDashboard, T.eventReports,
  T.users, T.profile, T.admin, T.logOut,
];

// Clinical + Admissions (142): ci (141) and clinical (144) merged.
const clinicalCi: string[] = [
  T.createReferral, T.referralInbox, T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.censusTrends, T.liveCensus,
  T.hospitalTracker, T.returningReferrals, T.adtReport, T.kpis, T.tripleCheckReport, T.averageLosReport,
  T.referralConversions, T.referralsSummary, T.assessmentReport, T.complaiReports, T.mdsReports, T.complaiDashboard,
  T.mdsDashboard, T.eventReports,
  T.profile, T.logOut,
];

const ci: string[] = [
  T.createReferral, T.referralInbox, T.managerDashboard, T.kpiDashboard, T.liveBedBoard, T.dischargePlan, T.censusTrends, T.liveCensus,
  T.hospitalTracker, T.returningReferrals, T.adtReport, T.kpis, T.averageLosReport,
  T.referralConversions, T.referralsSummary, T.profile, T.logOut,
];

const general: string[] = [
  T.createReferral, T.referralInbox, T.liveBedBoard, T.dischargePlan, T.censusTrends, T.liveCensus,
  T.hospitalTracker, T.returningReferrals, T.adtReport, T.kpis,
  T.referralConversions, T.profile, T.logOut,
];

/** Role 190: admin minus High Risk, Corporate Overview spliced in at index 1. */
const globalPortal: string[] = (() => {
  const titles = admin.filter((t) => t !== T.highRiskPatients);
  titles.splice(1, 0, T.corporateOverview);
  return titles;
})();

export const PRESET_TITLES: Record<RolePreset, readonly string[]> = {
  tripleCheck,
  socialWorker,
  mdsRegional,
  mdsNurse,
  minimalCi,
  admin,
  report,
  clinical,
  clinicalManager,
  clinicalCi,
  ci,
  general,
  globalPortal,
};

export function presetForRole(roleValue: number): RolePreset {
  const role = Number(roleValue);
  if (role === ROLE_NUMBER_TRIPLE_CHECK) { return 'tripleCheck'; }
  if (role === ROLE_NUMBER_SOCIAL_WORKER) { return 'socialWorker'; }
  if (role === ROLE_NUMBER_MDS_REGIONAL) { return 'mdsRegional'; }
  if (role === ROLE_NUMBER_MDS_NURSE) { return 'mdsNurse'; }
  if (role === ROLE_NUMBER_MINIMAL_CI) { return 'minimalCi'; }
  if (role === ROLE_NUMBER_GLOBAL_ADMIN) { return 'globalPortal'; }
  if (role > 170) { return 'admin'; }
  if (role === ROLE_NUMBER_ADMINISTRATOR) { return 'report'; }
  if (role === ROLE_NUMBER_CI_MANAGER) { return 'admin'; }
  if (role === ROLE_NUMBER_CLINICAL) { return 'clinical'; }
  if (role === ROLE_NUMBER_CLINICAL_MANAGER) { return 'clinicalManager'; }
  if (role === ROLE_NUMBER_CLINICAL_CI) { return 'clinicalCi'; }
  if (role === ROLE_NUMBER_CI) { return 'ci'; }
  return 'general';
}

/** A menu entry for KPI pages: the KPI root itself ("KPIs") or a page under it. */
export function isKpiTitle(title: string): boolean {
  const target = NAV_ROUTES[title];
  return target?.kind === 'internal' && (target.to === KPI_ROOT || target.to.startsWith(`${KPI_ROOT}/`));
}

/** Ordered titles for a role — legacy `titlesForRole` semantics, order included. */
export function titlesForRole(role: number, ctx: NavCtx): string[] {
  let titles = [...PRESET_TITLES[presetForRole(role)]];

  // CCARE hides Patient Assessment — legacy skips this for the >170 admin tier.
  if (role <= 170 && ctx.isCcare) {
    titles = titles.filter((t) => t !== T.assessmentReport);
  }
  if (!ctx.referralResponseReportEnabled) {
    titles = titles.filter((t) => t !== T.referralResponseReport);
  }

  if (!ctx.highRiskEnabled) {
    titles = titles.filter((t) => t !== T.highRiskPatients);
  }
  // Opt-in like High Risk: a paid option the client has to be granted.
  if (!ctx.dischargePlanEnabled) {
    titles = titles.filter((t) => t !== T.dischargePlan);
  }
  // The same rule as KpiRouteGuard, so the menu never offers a KPI page the guard bounces.
  // A ctx without the rule hides them from every role.
  if (!ctx.canViewKpis?.(role)) {
    titles = titles.filter((t) => !isKpiTitle(t));
  }
  return titles;
}
